import React, { useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { format } from "date-fns"
import { ChevronDown, Users } from "lucide-react"
import { toast } from "sonner"

import EventTable from "@/components/common/EventTable"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CardTitle } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useMarkAttendance } from "@/hooks/useMarkAttendance"
import { OrientationEvent, useOrientationEvents } from "@/hooks/useOrientationEvents"
import { SessionAttendee, useSessionAttendees } from "@/hooks/useSessionAttendees"
import { supabase } from "@/lib/supabase"
import { JobPosting } from "@/types/jobPosting"
import { getSupabaseFunctionErrorMessage } from "@/utils/supabaseFunctionUtils"

import EventDetailsDialog from "../events/EventDetailsDialog"
import GridEventDisplay from "../events/GridEventDisplay"
import RescheduleDialog from "../events/RescheduleDialog"

interface OrientationEventDisplayProps {
  viewMode: "grid" | "table"
  filteredEvents: OrientationEvent[]
  jobPostings: JobPosting[]
}

// Fallback data for when no attendees are available
const emptyAttendees: SessionAttendee[] = []

// Column definitions for the table view
const orientationColumns = [
  { header: "Title", accessor: "title" },
  { header: "Job Postings", accessor: "jobPostings" },
  { header: "Date", accessor: "date" },
  { header: "Time", accessor: "time" },
  { header: "Location", accessor: "location" },
  { header: "Capacity", accessor: "capacity" },
  { header: "Status", accessor: "status" },
  { header: "Attendees", accessor: "attendees" },
  { header: "Actions", accessor: "actions" },
]

const OrientationEventDisplay: React.FC<OrientationEventDisplayProps> = ({
  viewMode,
  filteredEvents,
  jobPostings,
}) => {
  const queryClient = useQueryClient()
  const [selectedEvent, setSelectedEvent] = useState<OrientationEvent | null>(null)
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false)
  const [selectedAttendee, setSelectedAttendee] = useState<string | null>(null)
  const [targetEvent, setTargetEvent] = useState<string | null>(null)
  const [isRescheduling, setIsRescheduling] = useState(false)

  // Fetch attendees for the selected event
  const { data: attendees = emptyAttendees, isLoading: isLoadingAttendees } = useSessionAttendees(
    selectedEvent?.id || null,
  )

  // Handle dialog open/close
  const handleOpenEvent = (event: OrientationEvent) => {
    setSelectedEvent(event)
  }

  const handleCloseEvent = () => {
    setSelectedEvent(null)
  }

  // Use the handleToggleStatus function from the hook
  const { handleToggleStatus: toggleStatus } = useOrientationEvents({
    jobPostings: [],
    locations: [],
  })

  // Handle toggling event status with toast notifications
  const handleToggleStatus = async (eventId: string, currentStatus: string) => {
    try {
      await toggleStatus(eventId, currentStatus)

      // Update the selected event if it's the one being modified
      if (selectedEvent && selectedEvent.id === eventId) {
        setSelectedEvent({
          ...selectedEvent,
          status: currentStatus === "Open" ? "Closed" : "Open",
        })
      }

      // Show success toast
      toast.success("Status Updated", {
        description: `Event status has been changed to ${
          currentStatus === "Open" ? "Closed" : "Open"
        }`,
      })
    } catch (error) {
      console.error("Error in handleToggleStatus:", error)
      toast.error("Error", {
        description: "An unexpected error occurred",
      })
    }
  }

  // Use the mark attendance mutation hook
  const { mutate: markAttendance } = useMarkAttendance()

  // Handle marking attendance
  const handleMarkAttendance = async (attendeeId: string, reset?: boolean) => {
    try {
      // Find the attendee to get the booking ID and application ID
      const attendee = attendees.find((a) => a.id === attendeeId)
      if (!attendee) return

      // Get the application ID from the attendee
      const applicationId = attendee.applicationId
      if (!applicationId) {
        console.error("No application ID found for attendee")
        toast.error("Error", {
          description: "Could not find application details",
        })
        return
      }

      // Determine the new attendance state
      let newAttendedState

      if (reset) {
        // If reset is true, set to null
        newAttendedState = null
      } else if (attendee.attended === null || attendee.attended === false) {
        // If currently null or false, set to true
        newAttendedState = true
      } else {
        // If currently true, set to false
        newAttendedState = false
      }

      // Call the mark-attendance function via our hook
      markAttendance({
        bookingId: attendee.bookingId,
        applicationId,
        attended: newAttendedState,
        eventType: "orientation",
      })
    } catch (error) {
      console.error("Error in handleMarkAttendance:", error)
      toast.error("Error", {
        description: "An unexpected error occurred",
      })
    }
  }

  // Handle reschedule dialog
  const handleOpenReschedule = (attendeeId: string) => {
    setSelectedAttendee(attendeeId)
    setRescheduleDialogOpen(true)
  }

  const handleCloseReschedule = () => {
    setRescheduleDialogOpen(false)
    setSelectedAttendee(null)
    setTargetEvent(null)
  }

  const handleReschedule = async () => {
    if (!selectedAttendee || !targetEvent) return

    setIsRescheduling(true)
    try {
      // Find the attendee and event
      const attendee = attendees.find((a) => a.id === selectedAttendee)
      const event = filteredEvents.find((e) => e.id === targetEvent)

      if (!attendee || !event) return

      // Call the centralized reschedule edge function to update booking and send emails
      const { data, error } = await supabase.functions.invoke("reschedule-session", {
        body: {
          bookingId: attendee.bookingId,
          targetSessionId: targetEvent,
        },
      })

      if (error) {
        console.error("Error rescheduling booking:", error)
        const message = await getSupabaseFunctionErrorMessage(error)
        toast.error("Error", {
          description: message,
        })
        return
      }

      if (!data?.success) {
        toast.error("Error", {
          description: data?.message || "Failed to reschedule the booking",
        })
        return
      }

      // Refetch sessions and attendees (source and target) to update counts and lists
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["sessions"] }),
        queryClient.invalidateQueries({
          queryKey: ["sessionAttendees", selectedEvent?.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ["sessionAttendees", targetEvent],
        }),
      ])

      toast.success("Rescheduled Successfully", {
        description: `${attendee.name} has been rescheduled to ${format(
          event.date,
          "PPP",
        )} at ${event.startTime} - ${event.endTime}`,
      })

      handleCloseReschedule()
    } catch (error) {
      console.error("Error in handleReschedule:", error)
      toast.error("Error", {
        description: "An unexpected error occurred",
      })
    } finally {
      setIsRescheduling(false)
    }
  }

  // Check if event is in progress
  const isEventInProgress = (event: OrientationEvent) => {
    const today = new Date()
    return event.date.toDateString() === today.toDateString()
  }

  // Render cell content for the table view
  const renderCell = (event: OrientationEvent, column: string) => {
    switch (column) {
      case "title":
        return <span className="font-medium">{event.title}</span>
      case "jobPostings":
        return (
          <div className="flex flex-wrap gap-1">
            {event.jobPostings.map((job) => (
              <Badge key={job} variant="secondary">
                {job}
              </Badge>
            ))}
          </div>
        )
      case "date": {
        // Format date without timezone conversion
        const date = event.date
        const day = date.getDate()
        const month = date.getMonth() + 1
        const year = date.getFullYear()
        return `${month.toString().padStart(2, "0")}/${day.toString().padStart(2, "0")}/${year}`
      }
      case "time":
        return `${event.startTime} - ${event.endTime}`
      case "location":
        return event.location
      case "capacity":
        return event.capacity
      case "status":
        return (
          <Badge
            variant={event.status === "Open" ? "default" : "secondary"}
            className="cursor-pointer"
            onClick={() => handleToggleStatus(event.id, event.status)}
          >
            {event.status}
          </Badge>
        )
      case "attendees":
        return (
          <div className="flex items-center">
            <Users className="h-4 w-4 mr-1" />
            <span>
              {event.registered} / {event.capacity}
            </span>
          </div>
        )
      case "actions":
        return (
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={() => handleOpenEvent(event)}>
              View
            </Button>
          </div>
        )
      default:
        return null
    }
  }

  // Render card content for the grid view
  const renderCardContent = (event: OrientationEvent) => {
    return (
      <>
        <CardTitle className="text-lg mb-2">{event.title}</CardTitle>
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div>
            <p className="text-xs text-palette-jungleGreen500">Date</p>
            <p className="font-medium">{format(event.date, "PPP")}</p>
          </div>
          <div>
            <p className="text-xs text-palette-jungleGreen500">Time</p>
            <p className="font-medium">
              {event.startTime} - {event.endTime}
            </p>
          </div>
          <div>
            <p className="text-xs text-palette-jungleGreen500">Location</p>
            <p className="font-medium">{event.location}</p>
          </div>
          <div>
            <p className="text-xs text-palette-jungleGreen500">Attendees</p>
            <p className="font-medium">
              <Users className="h-3 w-3 inline mr-1" />
              {event.registered} / {event.capacity}
            </p>
          </div>
          <div>
            <p className="text-xs text-palette-jungleGreen500">Status</p>
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Button variant="outline" size="sm" className="flex items-center gap-1 h-7 text-xs">
                  {event.status}
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onClick={() =>
                    event.status !== "Open" && handleToggleStatus(event.id, event.status)
                  }
                  className={event.status === "Open" ? "bg-accent text-accent-foreground" : ""}
                >
                  Open
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    event.status !== "Closed" && handleToggleStatus(event.id, event.status)
                  }
                  className={event.status === "Closed" ? "bg-accent text-accent-foreground" : ""}
                >
                  Closed
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div>
          <p className="text-xs text-palette-jungleGreen500 mb-1">Job Postings</p>
          <div className="flex flex-wrap gap-1">{renderJobPostings(event.jobPostings)}</div>
        </div>
      </>
    )
  }

  const renderJobPostings = (_jobPostings: string[]) => {
    // TODO: Should be map instead of array for faster lookup
    const jobPostingsArray = jobPostings.filter((jobPosting) =>
      _jobPostings.find((jobPostingId) => jobPosting.id === jobPostingId),
    )
    return jobPostingsArray.map((jobPosting) => (
      <Badge key={jobPosting.id} variant="secondary" className="pointer-events-none">
        {jobPosting.title}
      </Badge>
    ))
  }

  const formatEventOption = (event: OrientationEvent) => {
    return `${format(event.date, "PPP")} - ${event.startTime} - ${
      event.endTime
    } - ${event.location}`
  }

  // Render event details for the dialog
  const renderEventDetails = () => {
    if (!selectedEvent) return null

    return (
      <>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-palette-jungleGreen500">Title</p>
            <p className="font-medium">{selectedEvent.title}</p>
          </div>
          <div>
            <p className="text-sm text-palette-jungleGreen500">Date</p>
            <p className="font-medium">{format(selectedEvent.date, "PPP")}</p>
          </div>
          <div>
            <p className="text-sm text-palette-jungleGreen500">Time</p>
            <p className="font-medium">
              {selectedEvent.startTime} - {selectedEvent.endTime}
            </p>
          </div>
          <div>
            <p className="text-sm text-palette-jungleGreen500">Location</p>
            <p className="font-medium">{selectedEvent.location}</p>
          </div>
          <div>
            <p className="text-sm text-palette-jungleGreen500">Address</p>
            <p className="font-medium">{selectedEvent.locationAddress || "—"}</p>
          </div>
          <div>
            <p className="text-sm text-palette-jungleGreen500">Attendees</p>
            <p className="font-medium">
              {selectedEvent.registered} / {selectedEvent.capacity}
            </p>
          </div>
          <div>
            <p className="text-sm text-palette-jungleGreen500">Capacity</p>
            <p className="font-medium">{selectedEvent.capacity}</p>
          </div>
          <div>
            <p className="text-sm text-palette-jungleGreen500">Status</p>
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Button variant="outline" size="sm" className="flex items-center gap-1">
                  {selectedEvent.status}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onClick={() =>
                    selectedEvent.status !== "Open" &&
                    handleToggleStatus(selectedEvent.id, selectedEvent.status)
                  }
                  className={
                    selectedEvent.status === "Open" ? "bg-accent text-accent-foreground" : ""
                  }
                >
                  Open
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    selectedEvent.status !== "Closed" &&
                    handleToggleStatus(selectedEvent.id, selectedEvent.status)
                  }
                  className={
                    selectedEvent.status === "Closed" ? "bg-accent text-accent-foreground" : ""
                  }
                >
                  Closed
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium mb-2">Job Positions</h3>
          <div className="flex flex-wrap gap-1 mb-4">
            {renderJobPostings(selectedEvent.jobPostings)}
          </div>
        </div>
      </>
    )
  }

  if (viewMode === "grid") {
    return (
      <>
        <GridEventDisplay
          events={filteredEvents}
          onOpenEvent={handleOpenEvent}
          renderCardContent={renderCardContent}
          emptyMessage="No orientation events found for the selected filters."
        />

        <EventDetailsDialog
          isOpen={!!selectedEvent}
          onClose={handleCloseEvent}
          title="Orientation Details"
          event={selectedEvent}
          attendees={attendees}
          isLoadingAttendees={isLoadingAttendees}
          isEventInProgress={selectedEvent ? isEventInProgress(selectedEvent) : false}
          onMarkAttendance={handleMarkAttendance}
          onOpenReschedule={handleOpenReschedule}
          renderEventDetails={renderEventDetails}
          isOrientationEvent={true}
        />

        <RescheduleDialog
          isOpen={rescheduleDialogOpen}
          onClose={handleCloseReschedule}
          attendeeName={attendees.find((a) => a.id === selectedAttendee)?.name}
          events={filteredEvents}
          targetEventId={targetEvent}
          setTargetEventId={setTargetEvent}
          onReschedule={handleReschedule}
          formatEventOption={formatEventOption}
          loading={isRescheduling}
        />
      </>
    )
  }

  return (
    <>
      <EventTable
        columns={orientationColumns}
        data={filteredEvents}
        renderCell={(event, column) => renderCell(event, column)}
        emptyMessage="No orientation events found for the selected filters."
      />

      <EventDetailsDialog
        isOpen={!!selectedEvent}
        onClose={handleCloseEvent}
        title="Orientation Details"
        event={selectedEvent}
        attendees={attendees}
        isEventInProgress={selectedEvent ? isEventInProgress(selectedEvent) : false}
        onMarkAttendance={handleMarkAttendance}
        onOpenReschedule={handleOpenReschedule}
        renderEventDetails={renderEventDetails}
      />

      <RescheduleDialog
        isOpen={rescheduleDialogOpen}
        onClose={handleCloseReschedule}
        attendeeName={attendees.find((a) => a.id === selectedAttendee)?.name}
        events={filteredEvents}
        targetEventId={targetEvent}
        setTargetEventId={setTargetEvent}
        onReschedule={handleReschedule}
        formatEventOption={formatEventOption}
        loading={isRescheduling}
      />
    </>
  )
}

export default OrientationEventDisplay
